import { Ellipses, Td } from 'hatch-components';
import PropTypes from 'prop-types';
import React, { useRef, useState } from 'react';
import { useConnect } from 'redux-bundler-hook';
import ConfirmMenu from 'root/components/molecules/ConfirmMenu';
import OptionsMenu from 'root/components/molecules/OptionsMenu';
import { EDIT_MEMBER } from 'root/stores/routes/APP_URLS';
import timestampToFormattedDate from 'utils/timestampToFormattedDate';

import TeamWorkspaceList from '../TeamWorkspaceList';
import {
  StyledDisabled,
  StyledEditButton,
  StyledEditTd,
  StyledId,
  StyledMobilePrefix,
  StyledName,
  StyledNameTd,
  StyledResendEmailButton,
  StyledTr,
} from './styles';

const TeamRow = ({
  disabled,
  assignedBoardIDs,
  email,
  id,
  firstName,
  lastName,
  lastActiveAt,
  role,
  pending,
}) => {
  const [showMenu, setShowMenu] = useState(false);
  const [showConfirmDeactivate, setShowConfirmDeactivate] = useState(false);
  const ref = useRef(null);
  const {
    doCreateNotificationMessage,
    doInviteTeamMember,
    doUpdateQuery,
    currentOrganizationId,
    doDeactivateUser,
  } = useConnect(
    'doCreateNotificationMessage',
    'doDeactivateUser',
    'doInviteTeamMember',
    'doUpdateQuery',
    'selectCurrentOrganizationId',
  );

  const options = disabled
    ? []
    : [
        {
          onClick: () => {
            setShowMenu(false);
            doUpdateQuery({ drawer: EDIT_MEMBER, userId: id });
          },
          text: 'Edit',
        },
        {
          errorStyle: true,
          onClick: () => {
            setShowMenu(false);
            setShowConfirmDeactivate(true);
          },
          text: 'Deactivate',
        },
      ];

  return (
    <StyledTr disabled={disabled}>
      <StyledNameTd width="35%">
        <StyledName pending={pending}>
          {disabled ? <StyledDisabled>(Deactivated)</StyledDisabled> : null}
          {pending ? 'Pending Member' : `${firstName} ${lastName}`}
        </StyledName>
        <StyledId pending={pending}>{email ? `${email}` : '--'}</StyledId>
      </StyledNameTd>
      <Td>
        <StyledMobilePrefix label="Role:">{role || '--'}</StyledMobilePrefix>
      </Td>
      <Td>
        <StyledMobilePrefix label="Assigned Workspaces:" />
        <TeamWorkspaceList boardIDs={assignedBoardIDs} disabled={disabled} />
      </Td>
      <Td>
        {pending ? (
          <StyledResendEmailButton
            data-test-id="a76d780b-152b-4104-809e-55ed9e488998"
            onClick={() =>
              doInviteTeamMember(
                {
                  data: { assignedBoardIDs, email, firstName, lastName, role },
                  organizationId: currentOrganizationId,
                },
                (errors) => {
                  if (errors) {
                    doCreateNotificationMessage({
                      message:
                        'Unable to resend invitation email. Please try again.',
                      type: 'default',
                    });
                  } else {
                    doCreateNotificationMessage({
                      message: 'Re-sent invitation email.',
                      type: 'success',
                    });
                  }
                },
              )
            }
            variant="text"
          >
            Resend Email
          </StyledResendEmailButton>
        ) : (
          <StyledMobilePrefix label="Last Login:">
            {lastActiveAt ? timestampToFormattedDate(lastActiveAt, 'P') : 'N/A'}
          </StyledMobilePrefix>
        )}
      </Td>
      <StyledEditTd width={80}>
        {!disabled && !pending ? (
          <StyledEditButton
            data-test-id="636640b9-00e9-486c-a063-40fce4b2cd3d"
            onClick={() => setShowMenu(!showMenu)}
            ref={ref}
            variant="empty"
          >
            <Ellipses />
          </StyledEditButton>
        ) : null}

        <OptionsMenu
          onClose={() => setShowMenu(false)}
          options={options}
          targetRef={ref}
          visible={showMenu}
        />

        <ConfirmMenu
          actionButtonTitle="Deactivate"
          onActionButton={() => {
            setShowConfirmDeactivate(!showConfirmDeactivate);
            doDeactivateUser({
              organizationId: currentOrganizationId,
              userId: id,
            });
          }}
          onCancel={() => setShowConfirmDeactivate(!showConfirmDeactivate)}
          showOnMobile
          subtitle="Are you sure you want to deactivate this member?"
          targetRef={ref}
          title="Deactivate Member"
          visible={showConfirmDeactivate}
        />
      </StyledEditTd>
    </StyledTr>
  );
};
TeamRow.propTypes = {
  assignedBoardIDs: PropTypes.arrayOf(PropTypes.string),
  disabled: PropTypes.bool,
  email: PropTypes.string,
  firstName: PropTypes.string,
  id: PropTypes.string.isRequired,
  lastActiveAt: PropTypes.number,
  lastName: PropTypes.string,
  pending: PropTypes.bool,
  role: PropTypes.string,
};

TeamRow.defaultProps = {
  assignedBoardIDs: [],
  disabled: false,
  email: undefined,
  firstName: undefined,
  lastActiveAt: undefined,
  lastName: undefined,
  pending: false,
  role: undefined,
};

export default TeamRow;
